import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import AdminNavbar from '../../../components/feature/AdminNavbar';

interface RepairResult {
  total: number;
  linked: number;
  created: number;
  failed: number;
  details: Array<{
    investor: string;
    action: string;
    message: string;
  }>;
}

export default function RepairInvestorsAuthPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<RepairResult | null>(null);
  const [error, setError] = useState('');

  const handleRepairAll = async () => {
    if (!confirm('⚠️ هل أنت متأكد من إصلاح جميع حسابات المستثمرين؟\n\nسيتم:\n- ربط الحسابات الموجودة\n- إنشاء حسابات جديدة بكلمات مرور مؤقتة\n\nهذه العملية قد تستغرق بضع دقائق.')) {
      return;
    }

    setLoading(true);
    setError('');
    setResult(null);

    try {
      console.log('🔧 [إصلاح شامل] بدء عملية إصلاح جميع المستثمرين...');

      // الحصول على الجلسة الحالية
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData?.session?.access_token) {
        throw new Error('انتهت جلسة العمل - يرجى تسجيل الدخول مرة أخرى');
      }

      // استدعاء Edge Function
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/repair-all-investors-auth`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${sessionData.session.access_token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        console.error('❌ [إصلاح شامل] فشل:', data);
        throw new Error(data.error || 'فشل إصلاح الحسابات');
      }

      console.log('✅ [إصلاح شامل] تم بنجاح:', data.results);
      setResult(data.results);

    } catch (err: any) {
      console.error('❌ [إصلاح شامل] خطأ:', err);
      setError(err.message || 'حدث خطأ غير متوقع');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0e1a] via-[#1a1f35] to-[#0a0e1a] relative overflow-hidden" dir="rtl">
      {/* Background Grid */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(6,182,212,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(6,182,212,0.03)_1px,transparent_1px)] bg-[size:50px_50px] pointer-events-none"></div>
      
      {/* Navbar */}
      <AdminNavbar />

      {/* المحتوى */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8 pt-20 sm:pt-24">
        {/* العنوان */}
        <div className="bg-gradient-to-br from-dark-card to-dark-navy border border-gray-700/50 rounded-2xl p-6 mb-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-xl flex items-center justify-center flex-shrink-0">
              <i className="ri-tools-line text-3xl text-white"></i>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white mb-2">إصلاح شامل لحسابات المستثمرين</h1>
              <p className="text-gray-400">ربط جميع المستثمرين بحسابات Supabase Auth</p>
            </div>
          </div>

          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <i className="ri-information-line text-yellow-500 text-xl mt-0.5"></i>
              <div className="flex-1">
                <h3 className="text-yellow-500 font-bold mb-2">ماذا تفعل هذه الأداة؟</h3>
                <ul className="text-gray-300 text-sm space-y-1">
                  <li>• تفحص جميع المستثمرين الذين لا يملكون حساب Auth</li>
                  <li>• تربط الحسابات الموجودة بالبريد الإلكتروني</li>
                  <li>• تنشئ حسابات جديدة بكلمات مرور مؤقتة</li>
                  <li>• تحفظ رابط auth_user_id في جدول المستثمرين</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* رسالة خطأ */}
        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex items-start gap-3">
            <i className="ri-error-warning-line text-red-500 text-xl mt-0.5"></i>
            <div className="flex-1">
              <h3 className="text-red-500 font-bold mb-1">حدث خطأ</h3>
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          </div>
        )}

        {/* النتائج */}
        {result && (
          <div className="bg-gradient-to-br from-dark-card to-dark-navy border border-gray-700/50 rounded-2xl p-6 mb-6">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <i className="ri-checkbox-circle-line text-green-400"></i>
              نتائج الإصلاح
            </h2>

            {/* الإحصائيات */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                <div className="text-blue-400 text-sm mb-1">الإجمالي</div>
                <div className="text-white text-2xl font-bold">{result.total}</div>
              </div>
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                <div className="text-green-400 text-sm mb-1">تم الربط</div>
                <div className="text-white text-2xl font-bold">{result.linked}</div>
              </div>
              <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-4">
                <div className="text-cyan-400 text-sm mb-1">تم الإنشاء</div>
                <div className="text-white text-2xl font-bold">{result.created}</div>
              </div>
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                <div className="text-red-400 text-sm mb-1">فشل</div>
                <div className="text-white text-2xl font-bold">{result.failed}</div>
              </div>
            </div>

            {/* التفاصيل */}
            <div className="space-y-2">
              <h3 className="text-white font-bold mb-3">التفاصيل:</h3>
              <div className="max-h-96 overflow-y-auto space-y-2">
                {result.details.map((detail, index) => (
                  <div
                    key={index}
                    className={`p-3 rounded-lg border ${
                      detail.action === 'linked'
                        ? 'bg-green-500/10 border-green-500/30'
                        : detail.action === 'created'
                        ? 'bg-cyan-500/10 border-cyan-500/30'
                        : 'bg-red-500/10 border-red-500/30'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <i
                        className={`text-xl mt-0.5 ${
                          detail.action === 'linked'
                            ? 'ri-link text-green-400'
                            : detail.action === 'created'
                            ? 'ri-add-circle-line text-cyan-400'
                            : 'ri-error-warning-line text-red-400'
                        }`}
                      ></i>
                      <div className="flex-1">
                        <div
                          className={`font-bold mb-1 ${
                            detail.action === 'linked'
                              ? 'text-green-400'
                              : detail.action === 'created'
                              ? 'text-cyan-400'
                              : 'text-red-400'
                          }`}
                        >
                          {detail.investor}
                        </div>
                        <div className="text-gray-300 text-sm">{detail.message}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* ملاحظة للحسابات الجديدة */}
            {result.created > 0 && (
              <div className="mt-6 bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <i className="ri-information-line text-yellow-500 text-xl mt-0.5"></i>
                  <div className="flex-1">
                    <h4 className="text-yellow-500 font-bold mb-2">تنبيه هام</h4>
                    <p className="text-gray-300 text-sm">
                      تم إنشاء {result.created} حساب جديد بكلمات مرور مؤقتة.
                      يجب تحديث كلمات المرور من صفحة إدارة المستثمرين قبل إعطاء المستثمرين بيانات الدخول.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* زر الإصلاح */}
        <div className="bg-gradient-to-br from-dark-card to-dark-navy border border-gray-700/50 rounded-2xl p-6">
          <button
            onClick={handleRepairAll}
            disabled={loading}
            className="w-full px-6 py-4 bg-gradient-to-r from-yellow-500 to-orange-500 text-white font-bold text-lg rounded-lg hover:shadow-lg hover:shadow-yellow-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer"
          >
            {loading ? (
              <>
                <i className="ri-loader-4-line animate-spin text-2xl"></i>
                <span>جاري الإصلاح... هذا قد يستغرق بضع دقائق</span>
              </>
            ) : (
              <>
                <i className="ri-tools-line text-2xl"></i>
                <span>إصلاح جميع حسابات المستثمرين</span>
              </>
            )}
          </button>

          <div className="mt-4 flex items-center justify-center gap-4">
            <button
              onClick={() => navigate('/admin/investors')}
              className="text-gray-400 hover:text-white transition-colors flex items-center gap-2 cursor-pointer"
            >
              <i className="ri-arrow-right-line"></i>
              <span>العودة لإدارة المستثمرين</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
